/**
 * The Table plot type's configuration: what a measure and a group are, how they
 * read as words, how they travel in a link, and how they follow a derived
 * signal's rename or removal. Kept out of plot-state.ts the way number-config.ts
 * is, so the state module stays a list of setters.
 */
import type { ColumnInfo, TableAgg, TableBucket, TableGroup, TableMeasure, TableMode } from '../../api/types.ts';

/** A measure as the controls hold it: the wire fields plus how to show it. */
export interface TableMeasureState extends TableMeasure {
  /** Decimal places; null picks them from the value. Never sent. */
  decimals?: number | null;
}

/** Columns of a table. Mirrors backend `TABLE_MAX_MEASURES`. */
export const TABLE_MAX_MEASURES = 20;
/** Grouping levels. Mirrors backend `TABLE_MAX_GROUPS`. */
export const TABLE_MAX_GROUPS = 2;
/** Rows the backend returns. Mirrors backend `TABLE_MAX_ROWS`. */
export const TABLE_MAX_ROWS = 500;
/** Columns of a readings table. Mirrors backend `TABLE_MAX_COLUMNS`. */
export const TABLE_MAX_COLUMNS = 50;
/** The largest readings page. Mirrors backend `TABLE_MAX_READINGS`. */
export const TABLE_MAX_READINGS = 5000;
/** Rows per page a readings table offers. */
export const TABLE_PAGE_SIZES = [100, 250, 500, 1000, 2500, 5000];
export const TABLE_PAGE_SIZE_DEFAULT = 500;

/** The table-shaped slice of the plot state. */
export interface TableConfig {
  table_mode: TableMode;
  table_columns: string[];
  table_groups: TableGroup[];
  table_measures: TableMeasureState[];
  table_sort: string;
  /** Readings: the page shown (0-based) and how many readings a page holds. */
  table_page: number;
  table_page_size: number;
}

export interface TableAggInfo {
  value: TableAgg;
  label: string;
  /** Short form for a column header: "Avg", "P95". */
  short: string;
  /** Reads a signal. False for the time measures; count may take one or not. */
  column: 'required' | 'optional' | 'none';
  hint: string;
}

export const TABLE_AGGS: TableAggInfo[] = [
  { value: 'avg', label: 'Average', short: 'Avg', column: 'required', hint: 'Mean of the readings' },
  { value: 'median', label: 'Median', short: 'Median', column: 'required', hint: 'Middle reading' },
  { value: 'min', label: 'Minimum', short: 'Min', column: 'required', hint: 'Lowest reading' },
  { value: 'max', label: 'Maximum', short: 'Max', column: 'required', hint: 'Highest reading' },
  { value: 'sum', label: 'Sum', short: 'Sum', column: 'required', hint: 'All readings added up' },
  { value: 'stddev', label: 'Std deviation', short: 'Std dev', column: 'required', hint: 'Spread of the readings' },
  { value: 'p05', label: '5th percentile', short: 'P5', column: 'required', hint: '5% of readings are below' },
  { value: 'p95', label: '95th percentile', short: 'P95', column: 'required', hint: '95% of readings are below' },
  { value: 'first', label: 'First', short: 'First', column: 'required', hint: 'Earliest reading' },
  { value: 'last', label: 'Last', short: 'Last', column: 'required', hint: 'Latest reading' },
  { value: 'delta', label: 'Change (last − first)', short: 'Change', column: 'required', hint: 'For counters: running hours, fuel totals' },
  { value: 'count', label: 'Count', short: 'Count', column: 'optional', hint: 'Readings (of a signal, or rows)' },
  { value: 'hours', label: 'Hours', short: 'Hours', column: 'none', hint: 'Time covered, where the condition holds' },
  { value: 'share', label: '% of time', short: '% time', column: 'none', hint: 'Share of the time, where the condition holds' },
];

export function aggInfo(agg: TableAgg): TableAggInfo {
  return TABLE_AGGS.find((a) => a.value === agg) ?? TABLE_AGGS[0]!;
}

export const TABLE_BUCKETS: Array<{ value: TableBucket; label: string }> = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

/** The header a measure is shown under when it has no label of its own. */
export function measureLabel(m: TableMeasure): string {
  if (m.label?.trim()) return m.label.trim();
  const info = aggInfo(m.agg);
  const where = m.where?.trim() ? ` where ${m.where.trim()}` : '';
  if (m.agg === 'count' && !m.column) return `Rows${where}`;
  if (info.column === 'none') return `${info.label}${where}`;
  return `${info.short} ${m.column ?? ''}${where}`.trim();
}

export function groupLabel(g: TableGroup): string {
  if (g.kind === 'time') return TABLE_BUCKETS.find((b) => b.value === g.bucket)?.label ?? 'Day';
  if (g.kind === 'bands') return `${g.column ?? ''} band`;
  return g.column ?? '';
}

/** Unit suffix a value is read in, from the aggregate alone. */
export function measureUnit(m: TableMeasure): string {
  if (m.agg === 'hours') return 'h';
  if (m.agg === 'share') return '%';
  return '';
}

export function measureIssue(m: TableMeasure): string | null {
  const info = aggInfo(m.agg);
  if (info.column === 'required' && !m.column) return `${info.label} needs a signal`;
  return null;
}

export function groupIssue(g: TableGroup): string | null {
  if (g.kind !== 'time' && !g.column) return g.kind === 'bands' ? 'Bands need a signal' : 'Pick a column to group by';
  return null;
}

export function isTableReady(
  groups: TableGroup[],
  measures: TableMeasure[],
  mode: TableMode = 'summary',
  columns: string[] = [],
): boolean {
  if (mode === 'readings') return columns.length > 0 && columns.length <= TABLE_MAX_COLUMNS;
  return (
    measures.length > 0 &&
    measures.length <= TABLE_MAX_MEASURES &&
    groups.length <= TABLE_MAX_GROUPS &&
    measures.every((m) => !measureIssue(m)) &&
    groups.every((g) => !groupIssue(g))
  );
}

/** The wire form: display-only fields dropped, empty ones left out. */
export function measuresForRequest(measures: TableMeasureState[]): TableMeasure[] {
  return measures.map((m) => {
    const out: TableMeasure = { agg: m.agg };
    if (m.column && aggInfo(m.agg).column !== 'none') out.column = m.column;
    if (m.where?.trim()) out.where = m.where.trim();
    if (m.label?.trim()) out.label = m.label.trim();
    return out;
  });
}

export function groupsForRequest(groups: TableGroup[]): TableGroup[] {
  return groups.map((g) => {
    if (g.kind === 'time') return { kind: 'time', bucket: g.bucket ?? 'day' };
    if (g.kind === 'column') return { kind: 'column', column: g.column ?? null };
    const out: TableGroup = { kind: 'bands', column: g.column ?? null, bands: g.bands ?? 5 };
    if (g.band_edges?.length) out.band_edges = [...g.band_edges];
    return out;
  });
}

/* ── Links ──────────────────────────────────────────────── */

export function serializeTable(s: TableConfig, params: URLSearchParams): void {
  if (s.table_mode === 'readings') {
    if (s.table_columns.length) params.set('tcols', s.table_columns.join(','));
    if (s.table_sort) params.set('tsort', s.table_sort);
    if (s.table_page_size !== TABLE_PAGE_SIZE_DEFAULT) params.set('tps', String(s.table_page_size));
    if (s.table_page > 0) params.set('tpg', String(s.table_page + 1));
    return;
  }
  params.set('tmode', 'summary');
  if (s.table_groups.length) params.set('tg', JSON.stringify(groupsForRequest(s.table_groups)));
  if (s.table_measures.length) {
    params.set(
      'tm',
      JSON.stringify(
        s.table_measures.map((m) => {
          const wire = measuresForRequest([m])[0]!;
          return m.decimals != null ? { ...wire, dp: m.decimals } : wire;
        }),
      ),
    );
  }
  if (s.table_sort) params.set('tsort', s.table_sort);
}

/**
 * `tmode`: a summary says so; readings are the default. A link from before
 * readings existed carries measures and no mode, and is a summary.
 */
export function parseTableMode(params: URLSearchParams): TableMode {
  const mode = params.get('tmode');
  if (mode === 'summary' || mode === 'readings') return mode;
  return params.get('tm') ? 'summary' : 'readings';
}

/** `tps` and `tpg` (1-based in the link, as people count pages). */
export function parseTablePaging(params: URLSearchParams): { size: number; page: number } {
  const size = Number(params.get('tps'));
  const page = Number(params.get('tpg'));
  return {
    size: TABLE_PAGE_SIZES.includes(size) ? size : TABLE_PAGE_SIZE_DEFAULT,
    page: Number.isInteger(page) && page >= 1 ? page - 1 : 0,
  };
}

/**
 * The readings request's sort: `m<i>` names a picked column, anything else is
 * time. Sent to the server, so a sort covers every reading, not one page.
 */
export function readingsOrder(sort: string, columns: string[]): { table_order: string | null; table_desc: boolean } {
  const [id, dir] = sort.split(':');
  const desc = dir === 'desc';
  if (id?.startsWith('m')) {
    const name = columns[Number(id.slice(1))];
    if (name) return { table_order: name, table_desc: desc };
  }
  return { table_order: null, table_desc: id === 'k0' && desc };
}

const MONTH_MS = 30.4375 * 24 * 3600 * 1000;

/**
 * About how many readings a range holds, from the ship's metadata alone - no
 * query. The time column's count spread evenly over the months it covers,
 * taken for the part of each month the range touches. Before any filter, so it
 * is an upper bound once filters apply. Null when the metadata has no count.
 */
export function estimateReadings(columns: ColumnInfo[], start: string, end: string): number | null {
  const time = columns.find((c) => c.name === 'timestampNDC');
  // The app's dates are UTC with no zone written; read them as UTC.
  const utc = (v: string): number => Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v}Z`);
  const a = utc(start);
  const b = utc(end);
  if (!time?.months?.length || !time.total_count || !Number.isFinite(a) || !Number.isFinite(b) || b <= a) return null;
  const perMonth = time.total_count / time.months.length;
  const covered = new Set(time.months);
  let total = 0;
  const d = new Date(a);
  let cursor = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  while (cursor < b) {
    const m = new Date(cursor);
    const next = Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + 1, 1);
    const key = `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, '0')}`;
    if (covered.has(key)) {
      const overlap = Math.min(b, next) - Math.max(a, cursor);
      total += perMonth * (overlap / (next - cursor));
    }
    cursor = next;
  }
  return Math.round(total);
}

/** "≈ 14,600" - an estimate rounded so it does not pretend to be exact. */
export function roughCount(n: number): string {
  if (n < 1000) return n.toLocaleString('en-US');
  const step = n < 10000 ? 100 : n < 100000 ? 1000 : 10000;
  return (Math.round(n / step) * step).toLocaleString('en-US');
}

/** `tcols`: the readings table's columns. */
export function parseTableColumns(raw: string | null): string[] | null {
  if (!raw) return null;
  return [...new Set(raw.split(',').map((c) => c.trim()).filter(Boolean))].slice(0, TABLE_MAX_COLUMNS);
}

const AGG_SET = new Set(TABLE_AGGS.map((a) => a.value));
const BUCKET_SET = new Set(TABLE_BUCKETS.map((b) => b.value));

function str(v: unknown, max: number): string | null {
  return typeof v === 'string' && v ? v.slice(0, max) : null;
}

/** Parse `tg`; anything malformed is dropped rather than half-applied. */
export function parseTableGroups(raw: string | null): TableGroup[] | null {
  if (!raw) return null;
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return null; }
  if (!Array.isArray(data)) return null;
  const out: TableGroup[] = [];
  for (const g of data.slice(0, TABLE_MAX_GROUPS)) {
    if (!g || typeof g !== 'object') continue;
    const o = g as Record<string, unknown>;
    if (o.kind === 'time') {
      out.push({ kind: 'time', bucket: BUCKET_SET.has(o.bucket as TableBucket) ? (o.bucket as TableBucket) : 'day' });
    } else if (o.kind === 'column' || o.kind === 'bands') {
      const column = str(o.column, 250);
      if (!column) continue;
      if (o.kind === 'column') { out.push({ kind: 'column', column }); continue; }
      const bands = Number(o.bands);
      const edges = Array.isArray(o.band_edges)
        ? o.band_edges.map(Number).filter((n) => Number.isFinite(n))
        : null;
      out.push({
        kind: 'bands',
        column,
        bands: Number.isInteger(bands) && bands >= 2 && bands <= 12 ? bands : 5,
        band_edges: edges?.length ? edges : null,
      });
    }
  }
  return out;
}

/** Parse `tm`. */
export function parseTableMeasures(raw: string | null): TableMeasureState[] | null {
  if (!raw) return null;
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return null; }
  if (!Array.isArray(data)) return null;
  const out: TableMeasureState[] = [];
  for (const m of data.slice(0, TABLE_MAX_MEASURES)) {
    if (!m || typeof m !== 'object') continue;
    const o = m as Record<string, unknown>;
    if (!AGG_SET.has(o.agg as TableAgg)) continue;
    const measure: TableMeasureState = { agg: o.agg as TableAgg };
    const column = str(o.column, 250);
    if (column) measure.column = column;
    const where = str(o.where, 1000);
    if (where) measure.where = where;
    const label = str(o.label, 80);
    if (label) measure.label = label;
    const dp = Number(o.dp);
    if (o.dp != null && Number.isInteger(dp) && dp >= 0 && dp <= 10) measure.decimals = dp;
    out.push(measure);
  }
  return out;
}

/* ── Column references ──────────────────────────────────── */

/** Every column name a table reads outside its conditions. */
export function tableColumns(groups: TableGroup[], measures: TableMeasure[]): string[] {
  const out = new Set<string>();
  for (const g of groups) if (g.kind !== 'time' && g.column) out.add(g.column);
  for (const m of measures) if (m.column) out.add(m.column);
  return [...out];
}

function quoted(name: string): string {
  return JSON.stringify(name);
}

/** Drop columns, measures and groups that read a column not in `avail`. */
export function pruneTable(
  s: Pick<TableConfig, 'table_columns' | 'table_groups' | 'table_measures'>,
  avail: Set<string>,
): Pick<TableConfig, 'table_columns' | 'table_groups' | 'table_measures'> | null {
  const columns = s.table_columns.filter((c) => avail.has(c));
  const groups = s.table_groups.filter((g) => g.kind === 'time' || (g.column && avail.has(g.column)));
  const measures = s.table_measures.filter((m) => !m.column || avail.has(m.column));
  if (
    columns.length === s.table_columns.length &&
    groups.length === s.table_groups.length &&
    measures.length === s.table_measures.length
  ) return null;
  return { table_columns: columns, table_groups: groups, table_measures: measures };
}

/** A derived signal went away: drop what reads it, including in a condition. */
export function stripFromTable(
  groups: TableGroup[],
  measures: TableMeasureState[],
  name: string,
  columns: string[] = [],
): { table_columns: string[]; table_groups: TableGroup[]; table_measures: TableMeasureState[] } {
  const q = quoted(name);
  return {
    table_columns: columns.filter((c) => c !== name),
    table_groups: groups.filter((g) => g.column !== name),
    table_measures: measures.filter((m) => m.column !== name && !(m.where ?? '').includes(q)),
  };
}

/** A derived signal was renamed: follow it, including inside a condition. */
export function renameInTable(
  groups: TableGroup[],
  measures: TableMeasureState[],
  oldName: string,
  newName: string,
  columns: string[] = [],
): { table_columns: string[]; table_groups: TableGroup[]; table_measures: TableMeasureState[] } {
  const from = quoted(oldName);
  const to = quoted(newName);
  return {
    table_columns: columns.map((c) => (c === oldName ? newName : c)),
    table_groups: groups.map((g) => (g.column === oldName ? { ...g, column: newName } : g)),
    table_measures: measures.map((m) => {
      const next = m.column === oldName ? { ...m, column: newName } : { ...m };
      if (next.where?.includes(from)) next.where = next.where.split(from).join(to);
      return next;
    }),
  };
}

/** A first table from what the chart already shows: the average of each signal. */
export function seedMeasures(signals: string[]): TableMeasureState[] {
  return signals.slice(0, 6).map((column) => ({ agg: 'avg', column }));
}

/**
 * "Avg STW, Max STW +4 by Day" - what a stored table shows, from its link
 * parameters alone, for tile headers, default titles and the catalog.
 */
export function describeTable(params: URLSearchParams): string {
  if (parseTableMode(params) === 'readings') {
    const cols = parseTableColumns(params.get('tcols')) ?? [];
    if (!cols.length) return 'Table';
    return `${cols.slice(0, 3).join(', ')}${cols.length > 3 ? ` +${cols.length - 3}` : ''} readings`;
  }
  const measures = parseTableMeasures(params.get('tm')) ?? [];
  const groups = parseTableGroups(params.get('tg')) ?? [];
  if (!measures.length) return 'Table';
  const names = measures.slice(0, 2).map((m) => measureLabel(m));
  const more = measures.length > 2 ? ` +${measures.length - 2}` : '';
  const by = groups.length ? ` by ${groups.map(groupLabel).join(' × ')}` : '';
  return `${names.join(', ')}${more}${by}`;
}
